import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { router } from './router';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';

const AnalyticsContext = createContext<AnalyticsBrowser>(undefined!);

type Props = {
  writeKey: string;
  children: React.ReactNode;
};

export const useAnalyticsDefaultValues = () => {
  const { account, partBalance } = useArcadeStoreState();

  return {
    address: account?.address,
    network: account?.network,
    partBalance: partBalance,
  };
};

export const useAnalytics = () => {
  const result = useContext(AnalyticsContext);
  if (!result) {
    throw new Error('Context used outside of its Provider!');
  }
  return result;
};

export enum AnalyticsEvents {
  AdSplashViewed = 'Ad Splash Viewed',
  AdBannerViewed = 'Ad Banner Viewed',
  AdSplashClicked = 'Ad Splash Clicked',
  AdBannerClicked = 'Ad Banner Clicked',
  RewardCopied = 'Reward Copied',
  GoProClicked = 'Go Pro Clicked',
  StoreDetailsClicked = 'Store Details Clicked',
  GiveawaySubmissionStarted = 'Giveaway Submission Started',
  GiveawayEntryPurchaseSubmitted = 'Giveaway Entry Purchase Submitted',
}

export const AnalyticsProvider = ({ children, writeKey }: Props) => {
  const analytics = useMemo(
    () => AnalyticsBrowser.load({ writeKey }),
    [writeKey]
  );

  const { address, network, partBalance } = useAnalyticsDefaultValues();

  useEffect(() => {
    if (!analytics) return;

    const unsubscribe = router.subscribe('onLoad', (event) => {
      if (!event.hrefChanged) return;
      const matches = router.state.matches;
      const match = matches[matches.length - 1];
      /* @ts-ignore-next-line */
      const title = match.staticData.title ?? '';

      void analytics.page(title, {
        path: event.toLocation.pathname,
        search: event.toLocation.searchStr,
        address: address,
        network: network,
        partBalance: partBalance,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [analytics, address, network, partBalance]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};
